import React, { useState, useRef, useEffect } from "react";
import styles from "./energy-requirements.module.scss";
import { SelectedApplianceConfig } from "./energy-requirements.types";
import { PlusOnlyIcon } from "../../../../../assets/icons";
import { Button } from "../../../components";

interface EnergyRequirementsLandingProps {
	selectedAppliances: SelectedApplianceConfig[];
	onAddAppliance: () => void;
	onEditAppliance: (index: number) => void;
	onRemoveAppliance: (index: number) => void;
	onCancel: () => void;
	onContinue: () => void;
}

export const EnergyRequirementsLanding: React.FC<EnergyRequirementsLandingProps> = ({
	selectedAppliances,
	onAddAppliance,
	onEditAppliance,
	onRemoveAppliance,
	onCancel,
	onContinue,
}) => {
	const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);
	const menuRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
				setOpenMenuIndex(null);
			}
		};
		if (openMenuIndex !== null) {
			document.addEventListener("mousedown", handleClickOutside);
		}
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [openMenuIndex]);

	return (
		<div className={styles.container}>
			<div className={styles.content}>
				<div className={styles.header}>
					<div className={styles.iconWrapper}>
						<span className={styles.questionMark}>&#10067;</span>
					</div>
					<div className={styles.titleSection}>
						<h2 className={styles.title}>Tell Us Your Energy Requirements</h2>
						<p className={styles.subtitle}>Find and add your home appliances to calculate your energy usage</p>
					</div>
				</div>

				<button className={styles.addApplianceButton} onClick={onAddAppliance}>
					<span className={styles.addApplianceIcon}>
						<PlusOnlyIcon />
					</span>
					Add Appliance
				</button>

				{selectedAppliances.length > 0 && (
					<div className={styles.addedAppliancesList}>
						{selectedAppliances.map((config, index) => (
							<div key={`${config.applianceId}-${index}`} className={styles.addedApplianceItem}>
								<div className={styles.addedApplianceInfo}>
									{config.applianceIconUrl && <img src={config.applianceIconUrl} alt={config.applianceName} className={styles.addedApplianceIcon} />}
									<span className={styles.addedApplianceName}>{config.applianceName}</span>
								</div>
								<div className={styles.addedApplianceRight}>
									<div className={styles.categoryBadge}>
										<span className={styles.categoryBadgeCount}>
											{config.quantity}/{config.dailyUsage}
										</span>
									</div>
									<div className={styles.menuWrapper} ref={openMenuIndex === index ? menuRef : null}>
										<button className={styles.menuDotsButton} onClick={() => setOpenMenuIndex(openMenuIndex === index ? null : index)}>
											&middot;&middot;&middot;
										</button>
										{openMenuIndex === index && (
											<div className={styles.menuDropdown}>
												<button
													className={styles.menuDropdownItem}
													onClick={() => {
														onEditAppliance(index);
														setOpenMenuIndex(null);
													}}
												>
													Edit
												</button>
												<button
													className={`${styles.menuDropdownItem} ${styles.menuDropdownItemDanger}`}
													onClick={() => {
														onRemoveAppliance(index);
														setOpenMenuIndex(null);
													}}
												>
													Remove
												</button>
											</div>
										)}
									</div>
								</div>
							</div>
						))}
					</div>
				)}

				<div className={styles.landingFooter}>
					<div className={styles.landingFooterInner}>
						<Button variant="secondary" onClick={onCancel}>
							Cancel
						</Button>
						<Button variant="primary" onClick={onContinue} disabled={selectedAppliances.length === 0}>
							Continue
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};
